/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

@file:Suppress("TooManyFunctions")

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import systems.frictionless.impulse.browser.ErrorTranslation
import systems.frictionless.impulse.browser.composable.component.CenteredOnScreen
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.InlinedErrorMessage
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.composable.component.LoginFormContainer
import systems.frictionless.impulse.browser.composable.component.SideNavigationSection
import systems.frictionless.impulse.browser.composable.component.TextField
import systems.frictionless.impulse.browser.composable.component.Type
import systems.frictionless.impulse.browser.composable.component.WhiteBox
import systems.frictionless.impulse.browser.ext.id
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.browser.util.DO_NOTHING
import systems.frictionless.impulse.rest.model.Email
import systems.frictionless.impulse.rest.model.User
import systems.frictionless.impulse.viewmodel.ForgotPasswordViewModel
import systems.frictionless.impulse.viewmodel.NetworkViewModelState

@Composable
fun ForgotPassword(viewModel: ForgotPasswordViewModel) {
    val composableScope = rememberCoroutineScope()

    val onSubmit: (Email) -> Unit = {
        viewModel.submitResetRequest(
            composableScope,
            it
        )
    }

    ContentContainer(
        navigationSection = SideNavigationSection.SignIn,
        mainContainerCssClass = AppStyleSheet.login,
        mainContentContainerId = HtmlId.PageForgotPassword,
        mainContent = {
            CenteredOnScreen {
                LoginFormContainer {
                    when (val forgotPasswordState = viewModel.state.collectAsState().value) {
                        is NetworkViewModelState.Idle -> EmailForm(null, onSubmit)
                        is NetworkViewModelState.Busy -> ForgotPasswordProgress()
                        is NetworkViewModelState.Failure ->
                            EmailForm(
                                ErrorTranslation.translate(forgotPasswordState.failure) { "Server failure" },
                                onSubmit
                            )
                        is NetworkViewModelState.Complete -> Success()
                    }
                }
            }
        }
    )
}

@Composable
private fun EmailForm(
    errorMessage: String?,
    onSubmit: (Email) -> Unit
) {
    val (email, setEmail) = remember { mutableStateOf("") }

    WhiteBox(arrayOf(AppStyleSheet.narrowBox)) {
        ForgotPasswordHeader("What is your email address?")
        P {
            Text("You will receive an email with a link to reset your password.")
        }

        val submitForm = {
            onSubmit(Email.new(email))
        }

        Form(action = DO_NOTHING) {
            TextField(
                value = email,
                onValueChange = setEmail,
                type = Type.Email,
                onEnterKey = submitForm,
                autoFocus = true,
                maxLength = User.MAX_EMAIL_LENGTH,
                id = HtmlId.FormFieldEmail
            )
            errorMessage?.let {
                InlinedErrorMessage(it)
            }
            Br {}
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    id(HtmlId.PrimaryButton.idString)
                    if (!User.Builder.preflightEmail(email)) {
                        disabled()
                    }
                    onClick {
                        submitForm()
                    }
                }
            ) {
                Text("Next")
            }
        }
    }
}

@Composable
private fun Success() {
    Div(attrs = { id(HtmlId.ForgotPasswordSuccess) }) {
        P {
            Text("Please check your email for further instructions.")
        }
        P {
            Text("If you don't see a message arrive within 1-2 minutes, check your spam folder.")
        }
    }
}

@Composable
private fun ForgotPasswordHeader(headerText: String) {
    H1(attrs = { id(HtmlId.Headline) }) {
        Text(headerText)
    }
}

@Composable
private fun ForgotPasswordProgress() {
    LoadingIndicator()
}
